import { formatDateString } from "@/v2-common/utils/utils.date"
import Badge, { type BadgeProps } from "@/v2-backoffice-client/components/badge"
import LicenseBadgeTooltip, {
  hasLicenseTooltipFacts,
  type LicenseBadgeTooltipLicense,
} from "@/v2-backoffice-client/license/LicenseBadgeTooltip"
import {
  LICENSE_STATUS_EXPIRED,
  LICENSE_STATUS_NOT_RENEWING,
  LICENSE_STATUS_SCHEDULED,
} from "@/v2-backoffice-client/license/license.constants"
import {
  getLicenseStatusVisual,
  getTrialFacts,
} from "@/v2-backoffice-client/license/license.utils"

export type LicenseStatusBadgeProps = Omit<BadgeProps, "variant"> & {
  // Status + trial clock are the minimum (the user rows carry only those);
  // every extra lifecycle fact present upgrades the badge — the stamps need
  // the dates, the hover details render whatever else the caller's read
  // carries (license, seats, dates, source, CRM owner).
  license: LicenseBadgeTooltipLicense
  /** Status display name from the getLicenseStatuses lookup. */
  statusName?: string
}

/**
 * The date a status pill stamps after its name, so the board answers "since
 * when / until when?" without a hover: a trial its countdown, a scheduled
 * license its start, a not-renewing one its end, an expired one its end.
 * Active carries no date (it runs until someone ends it).
 */
function getStatusStamp(license: LicenseBadgeTooltipLicense): string | null {
  switch (license.LicenseStatusCode) {
    case LICENSE_STATUS_SCHEDULED:
      return license.StartedAt
        ? `from ${formatDateString(license.StartedAt)}`
        : null
    case LICENSE_STATUS_NOT_RENEWING: {
      const endsAt = license.CancelledAt ?? license.RenewsAt
      return endsAt ? `ends ${formatDateString(endsAt)}` : null
    }
    case LICENSE_STATUS_EXPIRED:
      return license.CancelledAt ? formatDateString(license.CancelledAt) : null
    default:
      return null
  }
}

// The license's status pill: the status's hue and icon (getLicenseStatusVisual,
// the one place both are decided) plus the date that matters for it. A trial
// carries its countdown ("Trial · 5 days left") and an overdue trial escalates
// to the red warning triangle; Scheduled says from when, Not renewing and
// Expired say until / since when. The hover holds the full lifecycle — trial
// length, renewal, end date, source, CRM owner — whenever the caller's read
// provides it.
function LicenseStatusBadge(props: LicenseStatusBadgeProps) {
  const { license, statusName, ...badgeProps } = props

  const trialFacts = getTrialFacts(license)
  const visual = getLicenseStatusVisual(license.LicenseStatusCode, {
    isTrialOverdue: trialFacts?.isOverdue,
  })

  const name = statusName ?? `Status ${license.LicenseStatusCode}`
  const stamp = trialFacts?.label ?? getStatusStamp(license)
  const label = stamp ? `${name} · ${stamp}` : name

  return (
    <Badge
      variant={visual.variant}
      iconSrc={visual.iconSrc}
      tooltip={
        hasLicenseTooltipFacts(license) ? (
          <LicenseBadgeTooltip license={license} statusName={name} />
        ) : undefined
      }
      {...badgeProps}
    >
      {label}
    </Badge>
  )
}

export default LicenseStatusBadge
